import { Component } from '@angular/core';
import { ProjectFeedService } from '../project-feed.service';
import {
  ObjectField,
  Project,
  ProjectFeed,
  ProjectFeedParseResult,
  ProjectObjectField,
  ProjectStatus
} from '../../models/project';
import { Client } from '../../models/client';
import { ClientRepository } from '../../client.service';
import { ProjectService } from '../project.service';
import { OptionService } from '../../shared/option.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalComponent } from '../../core/modal.component';
import { ActiveModal } from '../../core/modal.service';
import { ObjectFieldService } from '../../settings/object-field.service';

@Component({
  templateUrl: './create-project-modal.html'
})
export class CreateProjectModal extends ModalComponent {
  parent: Project;
  name: string;
  slug: string;
  client: Client;
  clients: Client[];
  projectTree: Project[];
  project: Project;
  loadingProjects: boolean = false;
  isExisting: boolean;
  isSaving: boolean = false;

  form: FormGroup;
  feedEnabled: boolean;
  isLoading: boolean = false;
  private parentShouldBeDisabled: boolean;

  isFetchingFeed: boolean;
  feedInfo: ProjectFeedParseResult;

  allFields: ObjectField[];

  constructor(public activeModal: ActiveModal,
              private projectService: ProjectService,
              private optionService: OptionService,
              private feedService: ProjectFeedService,
              private fieldService: ObjectFieldService,
              private fb: FormBuilder,
              private clientService: ClientRepository) {

    super(activeModal);
    this.clients = this.clientService.clients;
    this.createForm();
  }

  initExisting(project: Project) {
    this.isLoading = true;

    this.projectService.getProject(project.id).subscribe(p => {
      this.project = p;

      this.isExisting = true;

      this.init();

      this.setFormValues();
      this.isLoading = false;
    });
  }

  initNew(client?: Client, parent?: Project): void {
    if (client) {
      this.client = client;
      this.form.controls['clientId'].disable();
      this.form.controls['clientId'].setValue(client ? client.id : null);
    }

    if (parent) {
      this.parent = parent;
      this.parentShouldBeDisabled = true;
      this.form.controls['parentId'].disable();
      this.form.controls['parentId'].setValue(parent ? parent.id : null);
    }

    this.isExisting = false;

    this.project = new Project();

    this.init();
  }

  private init() {
    this.fieldService.get().subscribe((fields) => this.allFields = fields);
    this.opened = true;
  }

  setClient(client: number) {
    this.form.get('clientId').setValue(client);
  }

  loadProjectsTree() {
    if (!this.client) return;

    this.form.controls['parentId'].disable();

    this.loadingProjects = true;
    this.projectService.getProjectTreeForClient(this.client.id)
      .finally(() => {
        if (!this.parentShouldBeDisabled) {
          this.form.controls['parentId'].enable();
        }
      })
      .subscribe(projects => {
        this.projectTree = this.projectService.toFlatArray(projects);
      })
  }

  fetchFeed() {
    const url = this.form.get('feed.url').value,
      configuration = this.form.get('feed.configuration').value;

    this.isFetchingFeed = true;
    this.feedInfo = null;
    this.form.get('feed').disable();

    this.feedService.parseRawFeed(this.client, url, configuration)
      .finally(() => {
        this.isFetchingFeed = false;
        this.form.get('feed').enable();
      })
      .subscribe(data => {
        this.feedInfo = data;
        this.setFromFeedInfo(data);
      });
  }

  save() {
    if (!this.form.valid) {
      Object.keys(this.form.controls).forEach(field => {
        const control = this.form.get(field);
        control.markAsTouched({ onlySelf: true });
      });
      return;
    }

    this.isSaving = true;

    this.project = this.prepareSave();

    if (this.project.id) {
      this.projectService
        .update(this.project)
        .finally(() => this.isSaving = false)
        .subscribe(project => {
          this.activeModal.close(project);
        });
    } else {
      this.projectService
        .create(this.client.id, this.project, null)
        .finally(() => this.isSaving = false)
        .subscribe(project => {
          this.activeModal.close(project);
        });
    }
  }

  addField(field: ObjectField) {
    const projectField = new ProjectObjectField();
    projectField.field = field;
    this.project.objectFields.push(projectField);
  }

  removeField(field: ProjectObjectField) {
    let idx = this.project.objectFields.findIndex((f) => f.field.id === field.field.id);
    if (idx > -1) {
      this.project.objectFields.splice(idx, 1);
    }
  }

  isFieldActive(field: ObjectField) {
    let idx = this.project.objectFields.findIndex((f) => f.field.id === field.id);
    return idx > -1;
  }

  get statusOptions() {
    return this.optionService.projectStatusOptions;
  }

  get objectStatuses() {
    return this.optionService.objectStatusOptions;
  }

  private createForm() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      slug: ['', Validators.required],
      status: [ProjectStatus.Active, Validators.required],
      parentId: null,
      clientId: [null, Validators.required],
      feed: this.fb.group({
        enabled: [{ disabled: true, value: '' }],
        configuration: [null, null],
        url: ['', null]
      }),
      statuses: this.fb.group({})
    });

    const statusesGroup = this.form.get('statuses') as FormGroup;
    this.optionService.objectStatusOptions.forEach(status => {
      statusesGroup.addControl(status.value, new FormControl());
    });

    this.form.get('clientId').valueChanges.subscribe(clientId => {
      if (clientId) {
        this.client = this.clients.find(c => {
          return c.id === +clientId;
        });

        if (this.client) {
          this.form.get('feed.enabled').enable();
        }

        this.loadProjectsTree();
      }
    });

    this.form.get('parentId').valueChanges.subscribe(clientId => {

    });

    this.form.get('feed.enabled').valueChanges.subscribe(enabled => {
      this.feedEnabled = enabled;
      if (enabled) {
        this.form.get('feed.configuration').enable();
        this.form.get('feed.url').enable();
      } else {
        this.form.get('feed.configuration').disable();
        this.form.get('feed.url').disable();
      }
    })
  }

  defaultStatusName(status) {
    return this.optionService.objectStatusNames[status];
  }

  get statusFields() {
    const g = this.form.get('statuses') as FormGroup;
    return Object.keys(g.controls);
  }

  private setFormValues() {
    this.form.patchValue({
      name: this.project.name,
      slug: this.project.slug,
      parentId: this.project.parent ? this.project.parent.id : null,
      clientId: this.project.client.id,
      status: this.project.status,
      feed: {
        enabled: !!this.project.feed,
        configuration: this.project.feed ? this.project.feed.configuration.id : null,
        url: this.project.feed ? this.project.feed.url : ''
      },
      statuses: this.project.objectStatusNames
    });

  }

  private prepareSave() {
    const formModel = this.form.getRawValue();

    let project = new Project({
      id: this.project.id,
      name: formModel.name,
      slug: formModel.slug,
      status: formModel.status,
      url: this.project.url,
      parent: formModel.parentId ? new Project({ id: formModel.parentId }) : null,
      client: formModel.clientId ? new Client({ id: formModel.clientId }) : null,
    });

    project.objectFields = this.project.objectFields;
    project.objectStatusNames = formModel.statuses;

    if (formModel.feed.enabled) {
      project.feed = new ProjectFeed({
        url: formModel.feed.url,
        configuration: { id: formModel.feed.configuration }
      });
    }

    return project;
  }

  private setFromFeedInfo(data: ProjectFeedParseResult) {
    this.form.get('name').setValue(data.projectName);
    this.form.get('slug').setValue(data.projectSlug);
  }
}



// WEBPACK FOOTER //
// ./src/app/projects/create-projekt-modal/create-project-modal.ts